import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import {
  FLOOR_DEFAULT_COLOR,
  FLOOR_PLACEABLE_NFT,
  NFT_TEXTURE_ABOVE_BLACKOUT,
} from '../constants';
import {
  getCoordinatesFromId,
  getTileByCoordinate,
  isCoordinateAnNFT,
  isNFTAcceptedByTile,
} from '../utils/floor_utils';
import { Coordinate, FloorType } from '../types/floor';
import { useNFTContext } from '../context/NFTContext';
import { useConfigurationContext } from '../context/ConfigurationContext';
import { useFloorContext } from '../context/FloorContext';
import { useTargetContext } from '../context/TargetContext';

const Boxes = ({
  width,
  height,
  boxSize,
  floorMap,
}: {
  width: number;
  height: number;
  boxSize: number;
  floorMap: FloorType;
}) => {
  const {
    setSelectedNFT,
    setNFTPlacingCells,
    isNFTPlacingMode,
    nftPlacingModel,
    setNFTPlacingValid,
    setNFTPlaceableCellsAmountOnCurrentFloor,
  } = useNFTContext();
  const { target } = useTargetContext();
  const { currentFloor } = useFloorContext();
  const { tilesConfig } = useConfigurationContext();

  const refBoxesInstancedMesh = useRef<THREE.InstancedMesh>(null);
  const cubeTextureImage = require('../assets/2px_texture_grey.png');
  const cubeGeometry = useMemo(
    () => new THREE.BoxGeometry(boxSize, boxSize, boxSize),
    [boxSize]
  );

  const [shouldRefreshMaterial, setShouldRefreshMaterial] = useState(false);

  const [cubeMaterial, setCubeMaterial] =
    useState<THREE.MeshStandardMaterial>();

  const refreshMaterial = useCallback(() => {
    setCubeMaterial(
      new THREE.MeshStandardMaterial({
        map: new THREE.TextureLoader().load(cubeTextureImage, (tex) => {
          tex.magFilter = THREE.NearestFilter;
          tex.anisotropy = 16;
        }),
      })
    );
    setShouldRefreshMaterial(false);
  }, [cubeTextureImage]);

  useEffect(() => refreshMaterial(), [cubeTextureImage, refreshMaterial]);
  useEffect(() => setShouldRefreshMaterial(true), []);

  const cubeMesh = useMemo(
    () => new THREE.Mesh(cubeGeometry.clone(), cubeMaterial?.clone()),
    [cubeGeometry, cubeMaterial]
  );

  const willBoxResultInValidPlacement = useCallback(
    (id: number | undefined): { isValid: boolean; cells: Coordinate[] } => {
      if (isNFTPlacingMode && nftPlacingModel) {
        if (id !== undefined) {
          const temp = getCoordinatesFromId(id, width, height, true);
          const coordinates = {
            x: temp.x,
            y: temp.y,
          };

          setNFTPlacingCells([]);

          const modelWidth = nftPlacingModel.widthX;
          const modelHeight = nftPlacingModel.heightZ;

          let isValid = true;
          const cells: Coordinate[] = [];

          for (let i = 0; i < modelWidth; i++) {
            if (!isValid) break;
            for (let j = 0; j < modelHeight; j++) {
              if (!isValid) break;

              const currentX = coordinates.x + i;
              const currentY = coordinates.y + j;

              cells.push({
                startX: currentX,
                startY: currentY,
                endX: currentX,
                endY: currentY,
              });
              const nft = isCoordinateAnNFT(currentX, currentY, floorMap);
              const tile = getTileByCoordinate(currentX, currentY, floorMap);

              if (nft) {
                target === 'dev' && console.log('nft on the way');
                isValid = false;
              }

              if (isValid && !tile) {
                target === 'dev' &&
                  console.log('one cell is not on available tile');
                isValid = false;
              }

              if (isValid && tile && tilesConfig && nftPlacingModel) {
                if (!isNFTAcceptedByTile(tilesConfig, tile, nftPlacingModel)) {
                  target === 'dev' &&
                    console.log('nft level or type does not fit');
                  isValid = false;
                  console.log(tile, tilesConfig, nftPlacingModel.type);
                }
              }

              if (!tilesConfig) {
                console.error('No tiles config ever received from the server');
              }
            }
          }

          return { isValid, cells };
        }
      }

      return { isValid: false, cells: [] };
    },
    [
      floorMap,
      height,
      isNFTPlacingMode,
      nftPlacingModel,
      setNFTPlacingCells,
      target,
      tilesConfig,
      width,
    ]
  );

  const handleOnBoxClick = useCallback(
    (id: number | undefined) => {
      if (isNFTPlacingMode && nftPlacingModel) {
        if (id !== undefined) {
          const { isValid, cells } = willBoxResultInValidPlacement(id);
          setNFTPlacingValid(isValid);
          setNFTPlacingCells(cells);
        }
      }

      // empty box clicked, reset
      setSelectedNFT(undefined);
    },
    [
      isNFTPlacingMode,
      nftPlacingModel,
      setNFTPlacingCells,
      setNFTPlacingValid,
      setSelectedNFT,
      willBoxResultInValidPlacement,
    ]
  );

  useEffect(() => {
    if (isNFTPlacingMode) {
      handleOnBoxClick(0);
    }
  }, [currentFloor, handleOnBoxClick, isNFTPlacingMode]);

  useEffect(() => {
    if (refBoxesInstancedMesh.current) {
      target === 'dev' && console.log('Draw Boxes()');

      if (shouldRefreshMaterial) refreshMaterial();

      let counter = 0;
      let NFTPlaceableCells = 0;
      let autoPlacedBox = false;

      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          const id = counter++;

          const tile = getTileByCoordinate(x, y, floorMap);
          let nftPlaceable = false;

          if (tile && tile.type !== 'H' && tile.type !== 'E') {
            const position = {
              x: -y * boxSize,
              y: 0,
              z: x * boxSize,
            };

            // check if it should highlight tiles where you can place your room now
            nftPlaceable =
              (isNFTPlacingMode &&
                tilesConfig &&
                nftPlacingModel &&
                !isCoordinateAnNFT(x, y, floorMap) &&
                isNFTAcceptedByTile(tilesConfig, tile, nftPlacingModel)) ||
              false;

            if (nftPlaceable) {
              position.y = NFT_TEXTURE_ABOVE_BLACKOUT;
              NFTPlaceableCells++;

              if (!autoPlacedBox) {
                const { isValid } = willBoxResultInValidPlacement(id);
                if (isValid) {
                  handleOnBoxClick(id);
                  autoPlacedBox = true;
                }
              }
            }

            cubeMesh.position.set(position.x, position.y, position.z);
          } else {
            // remove this box from the view, it's not needed
            cubeMesh.position.set(
              Number.MAX_SAFE_INTEGER,
              Number.MAX_SAFE_INTEGER,
              Number.MAX_SAFE_INTEGER
            );
          }

          cubeMesh.updateMatrix();
          refBoxesInstancedMesh.current.setMatrixAt(id, cubeMesh.matrix);
          refBoxesInstancedMesh.current.setColorAt(
            id,
            nftPlaceable
              ? new THREE.Color(FLOOR_PLACEABLE_NFT)
              : new THREE.Color(FLOOR_DEFAULT_COLOR)
          );

          if (refBoxesInstancedMesh.current.instanceColor)
            refBoxesInstancedMesh.current.instanceColor.needsUpdate = true;
        }
      }

      setNFTPlaceableCellsAmountOnCurrentFloor(NFTPlaceableCells);
      refBoxesInstancedMesh.current.instanceMatrix.needsUpdate = true;
    }
  }, [
    boxSize,
    cubeMesh,
    floorMap,
    height,
    width,
    isNFTPlacingMode,
    tilesConfig,
    target,
    refreshMaterial,
    nftPlacingModel,
    shouldRefreshMaterial,
    setNFTPlaceableCellsAmountOnCurrentFloor,
    handleOnBoxClick,
    willBoxResultInValidPlacement,
  ]);

  return (
    <>
      <instancedMesh
        ref={refBoxesInstancedMesh}
        args={[undefined, undefined, width * height]}
        position={[0, 0, 0]}
        renderOrder={100}
        geometry={cubeMesh.geometry}
        material={cubeMesh.material}
        onClick={(e) => {
          e.stopPropagation();
          handleOnBoxClick(e.instanceId);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
        }}
      ></instancedMesh>
    </>
  );
};

export default Boxes;
